import { createFileRoute, redirect } from '@tanstack/react-router';
import { CancelledError } from '@tanstack/react-query';
import { ensureProcessDraft } from '@/api/mutations/useProcessDraft/lifecycle';
import { StartRecordingPage } from '@/pages/processes/recording/StartRecordingPage';
import { PendingProcessLoader } from '@/pages/processes/components/endless-process/PendingProcessLoader';
import { waitForPendingLoader } from '@/pages/processes/components/endless-process/finishGate';
import { createEntityBreadcrumbLoader } from '@/lib/entity-breadcrumb';
import { requireEditableProcess } from '@/lib/process-route-guards';

const breadcrumbLoader = createEntityBreadcrumbLoader('process', 'processId');

export const Route = createFileRoute('/$slug/processes/recording/start-recording/$processId')({
  beforeLoad: async ({ params, context }) => {
    const { status } = await requireEditableProcess(context.queryClient, params);
    // Starting publishes the draft; a process with live work already started.
    if (status !== 'not_started') {
      throw redirect({ to: '/$slug/processes/all-processes/$processId', params });
    }
  },
  loader: async (ctx) => {
    const { queryClient } = ctx.context;
    const { processId } = ctx.params;
    try {
      await ensureProcessDraft(queryClient, processId);
    } catch (err) {
      if (!(err instanceof CancelledError)) throw err;
      // A StrictMode remount cancelled the observer our fetch was sharing —
      // same retry as the editor loader.
      await Promise.resolve();
      await ensureProcessDraft(queryClient, processId);
    }
    const crumb = breadcrumbLoader(ctx);
    await waitForPendingLoader(processId, ctx.abortController.signal);
    return crumb;
  },
  pendingComponent: StartRecordingPending,
  pendingMs: 0,
  pendingMinMs: 0,
  // The loader POSTs a draft when none exists, so a hover preload must never run it.
  preload: false,
  component: StartRecordingPage
});

function StartRecordingPending() {
  const { processId } = Route.useParams();
  return (
    <PendingProcessLoader
      processId={processId}
      messageKey="viralRecording.status.loadingProcessData"
    />
  );
}
